import {
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Paper,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import classes from '../simulator.module.scss';
import React, { useState } from 'react';
import {
  CcfAccordion,
  CcfAccordionDetails,
  CcfAccordionSummary,
  CcfBox,
  CcfChatIcon,
  CcfDatePickerIcon,
  CcfDoubleArrowIcon,
  CcfEmailIcon,
  CcfPhoneInboundIcon,
  CcfSmsIcon,
  CcfTextField,
  CcfTypography,
} from '@nice-ccf/ui-controls';
import {
  interactionsHistory,
  getApplicationLocale,
} from '@nice-ccf/ui-components';
import { DropDown } from '../simulator';
import simulatorStyles from '../simulator.styles';
import { makeStyles, createStyles } from '@mui/styles';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useSelector } from 'react-redux';

/* eslint-disable-next-line */
export interface InteractionsHistoryCardProps {
  interactionHistoryList: interactionsHistory[] | undefined;
  handleInputChangeForInteractionHistory: (
    id: any | undefined,
    field: string,
    value: string
  ) => void;
  selectedContact: any;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      // minWidth: '270px',
      height: '100%',
      margin: '0',
      padding: '0',
      '&$expanded': {
        minHeight: '45px',
        margin: '0',
      },
    },
    content: {
      margin: '0',
      '&$expanded': {
        margin: '0',
      },
    },
    expanded: {
      margin: '0',
    },
    p10: {
      padding: '10px',
    },
    channelIcon: {
      fontSize: '18px',
    },
    summaryContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    ellipsis: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  })
);

/**
 * used to show interactions history card in simulator
 * @param props - InteractionsHistoryCardProps
 * @example ` <InteractionsHistoryCard />`
 */
export function InteractionsHistoryCard(props: InteractionsHistoryCardProps) {
  const {
    interactionHistoryList,
    handleInputChangeForInteractionHistory,
    selectedContact,
  } = props;
  const contactName = `${selectedContact?.customer.firstName} ${selectedContact?.customer.lastName}`;
  const styles = useStyles();
  const theme = useTheme();
  const simStyles = simulatorStyles(theme);
  const [expanded, setExpanded] = React.useState(null);
  const locale = useSelector(getApplicationLocale);

  /**
   * Used to display date picker icon
   * @example - DatePickerIcon()
   */
  const DatePickerIcon = () => {
    return (
      <CcfBox sx={{ display: 'flex' }}>
        <CcfDatePickerIcon />
      </CcfBox>
    );
  };

  /**
   * Used to handle input change
   * @example - handleChange()
   */
  const handleChange = (panel: any) => (_: any, isExpanded: any) => {
    setExpanded(isExpanded ? panel : null);
    setExpanded((state) => {
      return state;
    });
  };

  /**
   * to save interaction history  in local storage
   * @param args - none,
   * @example saveInteractionHistoryHandler()
   */
  const saveInteractionHistoryHandler = () => {
    localStorage.setItem(
      'interactionHistoryLS',
      JSON.stringify(interactionHistoryList)
    );
  };

  /**
   * Function to render Icon for Channel
   * @example renderIconForChannel()
   */
  const renderIconForChannel = (channel: string) => {
    switch (channel) {
      case 'Voice':
        return <CcfPhoneInboundIcon className={styles.channelIcon} />;
      case 'Email':
        return <CcfEmailIcon className={styles.channelIcon} />;
      case 'SMS':
        return <CcfSmsIcon className={styles.channelIcon} />;
      case 'Chat':
        return <CcfChatIcon className={styles.channelIcon} />;
      default:
        return null;
    }
  };

  return (
    <Grid style={{ width: '100%' }} className={classes.simulatorColumn}>
      <Paper elevation={3} className={classes.simulatorPaperWithFullheight}>
        <Card className={classes.heightInherit}>
          <CardHeader title={`Interactions History`} />
          {!interactionHistoryList ? (
            <CardContent className={classes.centerAlign}>
              <Typography color="textSecondary">
                No Interaction History.
              </Typography>
            </CardContent>
          ) : (
            <>
              <CardContent
                style={{ padding: '5px' }}
                className={
                  classes.simulatorHistoryContent + ' ' + classes.fixedHeight
                }
              >
                {interactionHistoryList?.map((interactionHistory, index) => {
                  const interactionHistoryId = interactionHistory.interactionId;
                  return (
                    <CcfAccordion
                      square
                      sx={simStyles.accordionContainer}
                      expanded={expanded === interactionHistory.interactionId}
                      onChange={handleChange(interactionHistory.interactionId)}
                    >
                      <CcfAccordionSummary
                        expandIcon={
                          <CcfDoubleArrowIcon sx={simStyles.expandedIcon} />
                        }
                        aria-controls="detail-content"
                        id={`sm_interactionHistory_${contactName}_contactName`}
                        sx={simStyles.accordionHeader}
                        classes={{ content: styles.summaryContent }}
                      >
                        {renderIconForChannel(interactionHistory.media)}
                        <CcfTypography className={styles.ellipsis}>
                          {interactionHistory.media === 'Email'
                            ? selectedContact?.customer.emailId
                            : selectedContact?.customer.phoneNo}
                        </CcfTypography>
                      </CcfAccordionSummary>
                      <CcfAccordionDetails classes={{ root: styles.root }}>
                        <CardActionArea key={'index'}>
                          <CardContent style={{ padding: '0px' }}>
                            <div
                              id={`sm_interactionHistory_${contactName}_date`}
                            >
                              <LocalizationProvider
                                adapterLocale={locale}
                                dateAdapter={AdapterDayjs}
                              >
                                <DateTimePicker
                                  label="Date"
                                  value={dayjs(interactionHistory.date)}
                                  onChange={(newValue: any) =>
                                    handleInputChangeForInteractionHistory(
                                      interactionHistory.interactionId,
                                      'date',
                                      newValue.toISOString()
                                    )
                                  }
                                  renderInput={(props: any) => (
                                    <CcfTextField
                                      fullWidth
                                      variant="outlined"
                                      color="primary"
                                      style={{ marginBottom: '15px' }}
                                      className={classes.input}
                                      InputProps={{
                                        classes: { input: classes.p10 },
                                      }}
                                      id="date"
                                      InputLabelProps={{ shrink: true }}
                                      {...props}
                                    />
                                  )}
                                  components={{
                                    OpenPickerIcon: DatePickerIcon,
                                  }}
                                />
                              </LocalizationProvider>
                            </div>
                            <DropDown
                              {...{
                                isFromInteractionHistory: true,
                                customerHistoryId:
                                  interactionHistory.interactionId,
                                currentValue: interactionHistory.media,
                                field: 'media',
                                label: 'Media',
                                options: ['Email', 'Voice', 'Chat', 'SMS'],
                                handleInputChangeForInteractionHistory,
                                id: `sm_interactionHistory_${contactName}_media_${interactionHistory.media}`,
                                stackName: `interactionHistory_${contactName}_media`,
                              }}
                            />
                            <br />
                            <DropDown
                              {...{
                                isFromInteractionHistory: true,
                                customerHistoryId:
                                  interactionHistory.interactionId,
                                currentValue:
                                  interactionHistory.status
                                    .charAt(0)
                                    .toUpperCase() +
                                  interactionHistory.status.slice(1),
                                field: 'status',
                                label: 'Status',
                                options: [
                                  'Open',
                                  'Pending',
                                  'Resolved',
                                  'Closed',
                                ],
                                handleInputChangeForInteractionHistory,
                                id: `sm_interactionHistory_${contactName}_status_${interactionHistory.status}`,
                                stackName: `interactionHistory_${contactName}_status`,
                              }}
                            />
                            <br />
                            <br />
                            <CcfTextField
                              fullWidth
                              color="primary"
                              id={`sm_interactionHistory_${contactName}_endTime`}
                              style={{ marginBottom: '15px' }}
                              className={classes.input}
                              InputLabelProps={{ shrink: true }}
                              InputProps={{ classes: { input: classes.p10 } }}
                              value={interactionHistory.endTime}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                handleInputChangeForInteractionHistory(
                                  interactionHistoryId,
                                  'endTime',
                                  event?.target.value
                                )
                              }
                              label={'End Time'}
                              variant="outlined"
                            ></CcfTextField>
                            <br />
                            <CcfTextField
                              fullWidth
                              color="primary"
                              id={`sm_interactionHistory_${contactName}_contactTrackingId`}
                              style={{ marginBottom: '15px' }}
                              className={classes.input}
                              InputLabelProps={{ shrink: true }}
                              InputProps={{ classes: { input: classes.p10 } }}
                              value={interactionHistory.contactTrackingId}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                handleInputChangeForInteractionHistory(
                                  interactionHistoryId,
                                  'contactTrackingId',
                                  event?.target.value
                                )
                              }
                              label={'Contact Tracking Id'}
                              variant="outlined"
                            ></CcfTextField>
                            <br />
                            <CcfTextField
                              fullWidth
                              color="primary"
                              id={`sm_interactionHistory_${contactName}_contactRequest`}
                              style={{ marginBottom: '15px' }}
                              className={classes.input}
                              InputLabelProps={{ shrink: true }}
                              InputProps={{ classes: { input: classes.p10 } }}
                              value={interactionHistory.contactRequest}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                handleInputChangeForInteractionHistory(
                                  interactionHistoryId,
                                  'contactRequest',
                                  event?.target.value
                                )
                              }
                              label={'Contact Request'}
                              variant="outlined"
                            ></CcfTextField>
                            <br />
                            <CcfTextField
                              fullWidth
                              color="primary"
                              id={`sm_interactionHistory_${contactName}_agentName`}
                              style={{ marginBottom: '15px' }}
                              className={classes.input}
                              InputLabelProps={{ shrink: true }}
                              InputProps={{ classes: { input: classes.p10 } }}
                              value={interactionHistory.agentName}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                handleInputChangeForInteractionHistory(
                                  interactionHistoryId,
                                  'agentName',
                                  event?.target.value
                                )
                              }
                              label={'Agent Name'}
                              variant="outlined"
                            ></CcfTextField>
                            <br />
                            <CcfTextField
                              fullWidth
                              color="primary"
                              id={`sm_interactionHistory_${contactName}_skill`}
                              style={{ marginBottom: '15px' }}
                              className={classes.input}
                              InputLabelProps={{ shrink: true }}
                              InputProps={{ classes: { input: classes.p10 } }}
                              value={interactionHistory.skill}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                handleInputChangeForInteractionHistory(
                                  interactionHistoryId,
                                  'skill',
                                  event?.target.value
                                )
                              }
                              label={'Skill'}
                              variant="outlined"
                            ></CcfTextField>
                            <br />
                            <CcfTextField
                              fullWidth
                              color="primary"
                              id={`sm_interactionHistory_${contactName}_campaign`}
                              style={{ marginBottom: '15px' }}
                              className={classes.input}
                              InputLabelProps={{ shrink: true }}
                              InputProps={{ classes: { input: classes.p10 } }}
                              value={interactionHistory.campaign}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                handleInputChangeForInteractionHistory(
                                  interactionHistoryId,
                                  'campaign',
                                  event?.target.value
                                )
                              }
                              label={'Campaign'}
                              variant="outlined"
                            ></CcfTextField>
                            <br />
                            <CcfTextField
                              fullWidth
                              color="primary"
                              id={`sm_interactionHistory_${contactName}_disposition`}
                              style={{ marginBottom: '15px' }}
                              className={classes.input}
                              InputLabelProps={{ shrink: true }}
                              InputProps={{ classes: { input: classes.p10 } }}
                              value={interactionHistory.disposition}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                handleInputChangeForInteractionHistory(
                                  interactionHistoryId,
                                  'disposition',
                                  event?.target.value
                                )
                              }
                              label={'Disposition'}
                              variant="outlined"
                            ></CcfTextField>
                            <br />
                            <CcfTextField
                              fullWidth
                              color="primary"
                              id={`sm_interactionHistory_${contactName}_autoSummary`}
                              style={{ marginBottom: '15px' }}
                              className={classes.input}
                              InputProps={{ classes: { input: classes.p10 } }}
                              InputLabelProps={{ shrink: true }}
                              value={interactionHistory.autoSummary}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                handleInputChangeForInteractionHistory(
                                  interactionHistoryId,
                                  'autoSummary',
                                  event?.target.value
                                )
                              }
                              label={'AutoSummary'}
                              variant="outlined"
                            ></CcfTextField>
                            <br />
                          </CardContent>
                        </CardActionArea>
                        <Divider />
                      </CcfAccordionDetails>
                    </CcfAccordion>
                  );
                })}
              </CardContent>
              <CardActions className={classes.simulatorSaveButton}>
                <Button
                  size="large"
                  variant="contained"
                  color="primary"
                  onClick={saveInteractionHistoryHandler}
                  id={`sm_interactionHistory_save`}
                >
                  Save
                </Button>
              </CardActions>
            </>
          )}
        </Card>
      </Paper>
    </Grid>
  );
}

export default InteractionsHistoryCard;
